"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import IpIcon from "./IpIcon";
import { challenges, industryTabs, latest, perspectives, trends, type IndustryKey } from "./industryData";

const d = (i: number, per = 0.07, cap = 6) => ({ "--d": `${(i % cap) * per}s` } as CSSProperties);

const Arrow = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);
const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 3l5 5-5 5" />
  </svg>
);

/* ---------- scroll slide-up reveal (scoped to .ip-page) ---------- */
function IpReveal() {
  useEffect(() => {
    const root = document.querySelector(".ip-page");
    if (!root) return;
    root.classList.add("ip-js");
    const SEL = ".ip-reveal, .ip-stagger, .ip-card";
    const show = (el: Element) => el.classList.add("is-visible");
    if (!("IntersectionObserver" in window)) {
      root.querySelectorAll(SEL).forEach(show);
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            show(e.target);
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    root.querySelectorAll(SEL).forEach((el) => io.observe(el));
    // cards re-mount when the industry filter changes
    const mo = new MutationObserver((muts) =>
      muts.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (!(n instanceof HTMLElement)) return;
          if (n.matches(SEL)) io.observe(n);
          n.querySelectorAll(SEL).forEach((el) => io.observe(el));
        })
      )
    );
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); };
  }, []);
  return null;
}

export default function IndustryBody() {
  const [filter, setFilter] = useState<IndustryKey | "all">("all");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);

  const shownPersp = useMemo(() => perspectives.filter((p) => filter === "all" || p.key === filter), [filter]);
  const shownLatest = useMemo(() => latest.filter((p) => filter === "all" || p.key === filter), [filter]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    // TODO: POST to your subscribe endpoint (e.g. /api/subscribe) before marking as done
    setDone(true);
  };

  return (
    <>
      <IpReveal />

      {/* INDUSTRY TAB BAR */}
      <div className="ip-tabbar ip-reveal" id="industries">
        <div className="ip-tabs ip-wrap" role="group" aria-label="Filter perspectives by industry">
          {industryTabs.map((t) =>
            t.href ? (
              <Link key={t.key} href={t.href} className="ip-tab">
                <IpIcon name={t.icon} size={30} />
                <span>{t.label}</span>
              </Link>
            ) : (
              <button
                key={t.key}
                type="button"
                className={`ip-tab${filter === t.key ? " is-active" : ""}`}
                aria-pressed={filter === t.key}
                onClick={() => setFilter(t.key as IndustryKey | "all")}
              >
                <IpIcon name={t.icon} size={30} />
                <span>{t.label}</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* FEATURED PERSPECTIVE */}
      <section className="ip-wrap ip-featured">
        <div className="ip-featured-copy ip-reveal">
          <p className="ip-kicker">FEATURED <b>PERSPECTIVE</b></p>
          <h2>
            The next wave of manufacturing:
            <br />
            <span>Data, AI and resilience.</span>
          </h2>
          <p className="ip-featured-text">
            As global manufacturing evolves, data, automation and AI are driving smarter operations, resilient supply chains and sustainable growth.
          </p>
          <Link href="/insights/industry-perspectives/the-next-wave-of-manufacturing" className="ip-btn ip-btn-primary">
            Read the full perspective <Arrow size={15} />
          </Link>
        </div>
        <div className="ip-featured-media ip-reveal">
          <div className="ip-featured-img" style={{ backgroundImage: "url('/assets/images/manufacturing.jpg')" }} role="img" aria-label="Manufacturing engineer reviewing live data and AI dashboards" />
        </div>
      </section>

      {/* PERSPECTIVES ACROSS INDUSTRIES */}
      <section className="ip-wrap ip-section" id="perspectives">
        <div className="ip-head ip-reveal">
          <div>
            <h2>Perspectives across industries</h2>
            <p>Explore insights, trends and opportunities for your industry.</p>
          </div>
          <Link href="/industries" className="ip-link">View all industries <Arrow /></Link>
        </div>
        <div className="ip-persp-grid" key={`p-${filter}`}>
          {shownPersp.map((p, i) => (
            <Link key={p.key} href={p.href} className="ip-card ip-persp" style={d(i)}>
              <span className="ip-persp-img"><span style={{ backgroundImage: `url('${p.image}')` }} /></span>
              <span className="ip-persp-body">
                <h3>{p.title}</h3>
                <p>{p.text}</p>
                <span className="ip-more">Explore insights <Arrow /></span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* KEY TRENDS */}
      <section className="ip-wrap ip-section">
        <div className="ip-head ip-reveal">
          <div>
            <h2>Key trends shaping industries</h2>
            <p>A snapshot of what&apos;s driving change across sectors.</p>
          </div>
          <Link href="/insights/trends" className="ip-link">Explore all trends <Arrow /></Link>
        </div>
        <div className="ip-trends ip-stagger">
          {trends.map((t) => (
            <article key={t.no} className="ip-trend">
              <span className="ip-trend-icon"><IpIcon name={t.icon} size={30} /></span>
              <div>
                <span className="ip-trend-no">{t.no}</span>
                <h3>{t.title}</h3>
                <p>{t.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* COMMON INDUSTRY CHALLENGES */}
      <section className="ip-wrap ip-section">
        <div className="ip-head ip-reveal">
          <div>
            <h2>Common industry challenges</h2>
            <p>Different industries. Similar challenges. New opportunities.</p>
          </div>
          <Link href="/solutions" className="ip-link">Explore how we help <Arrow /></Link>
        </div>
        <div className="ip-chal">
          <div className="ip-chal-media ip-reveal">
            <div className="ip-chal-img" style={{ backgroundImage: "url('/assets/images/business-problem.jpg')" }} role="img" aria-label="Two colleagues working through an industry challenge together" />
          </div>
          <ul className="ip-chal-list ip-stagger">
            {challenges.map((c) => (
              <li key={c.text}>
                <Link href="/solutions">
                  <span className="ip-chal-icon"><IpIcon name={c.icon} size={24} /></span>
                  <span className="ip-chal-text">{c.text}</span>
                  <span className="ip-chal-go"><Chevron /></span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* LATEST INDUSTRY PERSPECTIVES */}
      <section className="ip-wrap ip-section" id="latest">
        <div className="ip-head ip-reveal">
          <div>
            <h2>Latest industry perspectives</h2>
            <p>Fresh thinking, practical insights and real-world perspectives.</p>
          </div>
          <Link href="/insights" className="ip-link">View all insights <Arrow /></Link>
        </div>
        {shownLatest.length === 0 ? (
          <p className="ip-empty">New perspectives for this industry are coming soon.</p>
        ) : (
          <div className="ip-latest-grid" key={`l-${filter}`}>
            {shownLatest.map((a, i) => (
              <Link key={a.slug} href={``} className="ip-card ip-latest" style={d(i)}>
                <span className="ip-latest-img"><span style={{ backgroundImage: `url('${a.image}')` }} /></span>
                <span className="ip-latest-body">
                  <small>{a.category}</small>
                  <h3>{a.title}</h3>
                  <span className="ip-latest-foot">
                    <span>{a.read} <i>|</i> {a.date}</span>
                    <span className="ip-latest-go"><Arrow size={13} /></span>
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* NEWSLETTER */}
      <section className="ip-wrap ip-news-wrap ip-reveal" id="subscribe">
        <div className="ip-news">
          <div className="ip-news-copy">
            <p className="ip-news-kicker">STAY AHEAD</p>
            <h2>Get the latest industry insights, straight to your inbox.</h2>
            <p>Trends, perspectives and practical insights — curated for you.</p>
          </div>
          {/* <form className="ip-news-form" onSubmit={onSubmit}>
            <div className="ip-news-field">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setDone(false); }}
                placeholder="Enter your email address"
                aria-label="Email address"
              />
              <button type="submit" className="ip-btn ip-btn-primary">
                {done ? "Subscribed" : "Subscribe"} <Arrow size={15} />
              </button>
            </div>
            <small aria-live="polite">{done ? "Thank you — you're on the list." : "No spam. Just relevant insights."}</small>
          </form> */}
        </div>
      </section>
    </>
  );
}